import { useEffect, useMemo, useState, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ExpertDoc } from 'packages/types'
import {
  ResumeDetails,
  selectors as resumeDistributionSelectors,
  actions as resumeDistributionActions,
} from 'builder/modules/resumeDistribution'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { actions as resumeReviewActions } from 'builder/modules/resumeReview'
import {
  selectors as panelSelectors,
  actions as panelActions,
  BriefResume,
} from 'builder/modules/panel'
import { actions as renderingActions } from 'builder/modules/rendering'
import {
  DocumentTypes as TYPES,
  FormatType,
  FetchStatuses,
  DocumentTypes,
} from 'builder/modules/constants'
import {
  Questionnaire,
  ResumeDistributionLayout,
  ResumePreviewModal,
  useResumeDistributionABTest,
} from 'builder/components/ResumeDistributionV2'
import { ElementHighlight } from 'builder/components/DocumentEditor/ElementHighlight'
import { useI18n } from 'builder/hooks/useI18n'
import { actions } from 'builder/modules/resumeEditor'
import { ERDSteps } from '../../types'
import FormLayout from '../../components/FormLayout'
import PersonalDetailsForm from '../../components/PersonalDetailsForm'
import { JobPreferencesForm } from '../../components/JobPreferencesForm/JobPreferencesForm'
import { DATA_HIGHLIGHT_ID } from '../../components/PersonalDetailsForm/ResumePreview/ResumeBuilderPreview'

const RESUME_DISTRIBUTION_CHANGE_RESUME_HIGHLIGHT_SHOWN =
  'RESUME_DISTRIBUTION_CHANGE_RESUME_HIGHLIGHT_SHOWN'

const ResumeDistributionEditView = () => {
  const dispatch = useDispatch()
  const [query] = useSearchParams()
  const resumeId = query.get('resume_id')
  const documentType = query.get('document_type')
  const documentFormat = query.get('document_format')
  const documentTemplate = query.get('document_template')
  const location = useLocation()
  const navigate = useNavigate()
  const [activeStep, setActiveStep] = useState<ERDSteps>(ERDSteps.FORM_STEP_1)
  const selectedResumeDetails = useTypedSelector<ResumeDetails>(
    resumeDistributionSelectors.selectedResumeDetails,
  )
  const isResumePreviewOpen = useTypedSelector(resumeDistributionSelectors.isResumePreviewOpen)
  const { isResumeDistributionV2ABTestActive } = useResumeDistributionABTest()
  const { i18n } = useI18n()
  const documentsList = useTypedSelector(panelSelectors.documents)
  const [selectedResume, setSelectedResume] = useState<BriefResume | null>(null)
  const documentsStatus = useTypedSelector(panelSelectors.documentsStatus)
  const resumeList = useTypedSelector(store =>
    panelSelectors.documentsByType(store, DocumentTypes.resume),
  )
  const trDocuments = useTypedSelector(store =>
    panelSelectors.documentsByType(store, DocumentTypes.trDocument),
  )
  const lastDocumentRef = useRef<BriefResume | ExpertDoc | null>(null)
  const completeResume = useTypedSelector(resumeDistributionSelectors.formResumeFetchDetails)
  const isParsing = useTypedSelector<boolean>(resumeDistributionSelectors.isParsing)

  const trResumes = useMemo(
    () =>
      Array.isArray(trDocuments)
        ? trDocuments.filter(item => item.doc_type === DocumentTypes.resume)
        : [],
    [trDocuments],
  )

  useEffect(() => {
    dispatch(panelActions.fetchDocuments())
  }, [dispatch])

  useEffect(() => {
    if (
      isResumeDistributionV2ABTestActive &&
      documentsList?.length === 0 &&
      documentsStatus === FetchStatuses.loaded &&
      !completeResume &&
      !isParsing
    ) {
      navigate('/resume-distribution')
    }
  }, [
    completeResume,
    documentsList,
    documentsStatus,
    isParsing,
    isResumeDistributionV2ABTestActive,
    navigate,
  ])

  useEffect(() => {
    if (!isResumeDistributionV2ABTestActive) return

    if (resumeList?.length > 0 || trResumes?.length > 0) {
      const getLatestItem = () => {
        const latestResume = resumeList?.reduce((latest, current) => {
          return new Date(current.updatedAt ?? '') > new Date(latest.updatedAt ?? '')
            ? current
            : latest
        }, resumeList?.[0])

        const latestTrDoc = trResumes?.reduce((latest, current) => {
          return new Date(current.created_at) > new Date(latest.created_at) ? current : latest
        }, trResumes?.[0])

        if (!latestResume) return latestTrDoc
        if (!latestTrDoc) return latestResume

        return new Date(latestResume.updatedAt ?? '') > new Date(latestTrDoc.created_at)
          ? latestResume
          : latestTrDoc
      }

      const latestDocument = getLatestItem()

      if (JSON.stringify(lastDocumentRef.current) !== JSON.stringify(latestDocument)) {
        lastDocumentRef.current = latestDocument
        dispatch(resumeDistributionActions.setResumeDetails(latestDocument))
      }
    }
  }, [dispatch, isResumeDistributionV2ABTestActive, resumeList, trResumes])

  useEffect(() => {
    if (resumeId && documentType) {
      dispatch(
        renderingActions.download({
          id: Number(resumeId),
          type: documentType as TYPES,
          format: documentFormat as FormatType,
          template: documentTemplate as string,
        }),
      )

      if (documentType !== TYPES.coverLetter)
        dispatch(resumeReviewActions.sendResume({ resumeId: Number(resumeId) }))
      navigate(`${location.pathname}?resume_id=${resumeId}`, { replace: true })
    }
  }, [documentType, documentFormat, documentTemplate, resumeId, location, dispatch])

  useEffect(() => {
    if (
      !selectedResume &&
      documentsList.length &&
      resumeId &&
      !isResumeDistributionV2ABTestActive
    ) {
      const resume = documentsList.find(document => document.id === Number(resumeId))
      setSelectedResume(resume as BriefResume)
      dispatch(resumeDistributionActions.setResumeDetails(resume as BriefResume))
    }
  }, [documentsList, selectedResume, resumeId, dispatch])

  useEffect(() => {
    const isHighlightShown = localStorage.getItem(RESUME_DISTRIBUTION_CHANGE_RESUME_HIGHLIGHT_SHOWN)
    if (isResumeDistributionV2ABTestActive && !isHighlightShown) {
      dispatch(
        actions.setHighlightedElement({
          title: i18n.t(
            'builder.resume_distribution.version_3.questionnaire.resume_preview_box.highlighted_element.title',
          ),
          content: i18n.t(
            'builder.resume_distribution.version_3.questionnaire.resume_preview_box.highlighted_element.description',
          ),
          target: `[data-highlight-id=${DATA_HIGHLIGHT_ID}]`,
        }),
      )
    }
  }, [dispatch, i18n, isResumeDistributionV2ABTestActive, selectedResume])

  if (isResumeDistributionV2ABTestActive) {
    return (
      <>
        <Questionnaire />
        {isResumePreviewOpen && <ResumePreviewModal />}
        <ElementHighlight
          onClose={() => {
            localStorage.setItem(RESUME_DISTRIBUTION_CHANGE_RESUME_HIGHLIGHT_SHOWN, 'true')
          }}
        />
      </>
    )
  }

  switch (activeStep) {
    case ERDSteps.FORM_STEP_1:
      return (
        <ResumeDistributionLayout>
          <FormLayout activeStep={activeStep}>
            <PersonalDetailsForm
              resumePayload={selectedResumeDetails || selectedResume}
              setActiveStep={setActiveStep}
              setSelectedResumeDetails={(resumeDetails: ResumeDetails) => {
                dispatch(
                  resumeDistributionActions.setPersonalDetailsForm({
                    firstName: '',
                    lastName: '',
                    email: '',
                    jobTitle: '',
                    linkedin: '',
                    fullAddress: '',
                  }),
                )
                if (resumeDetails) {
                  dispatch(resumeDistributionActions.setResumeDetails(resumeDetails))
                }
              }}
            />
          </FormLayout>
        </ResumeDistributionLayout>
      )
    case ERDSteps.FORM_STEP_2:
      return (
        <ResumeDistributionLayout>
          <FormLayout activeStep={activeStep}>
            <JobPreferencesForm setActiveStep={setActiveStep} />
          </FormLayout>
        </ResumeDistributionLayout>
      )
    default:
      return <></>
  }
}

export default ResumeDistributionEditView
